/**
 * StepEditor Component
 * Dialog for editing an existing step in a schedule.
 * Supports both Action-type and Schedule-type (nested) steps.
 * For adding new steps, use AddStepDialog instead.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  Slider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  ImageOutlined as ImageIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  ScheduleStep,
  StepUpdate,
  CompletionAction,
  TimerStyle,
  Schedule,
  ScheduleType,
  DayOfWeek,
  SCHEDULE_VALIDATION,
} from '../../types/schedule';
import { Action } from '../../types/action';
import { scheduleService } from '../../services/scheduleService';
import { ActionPicker } from './ActionPicker';
import { SchedulePicker } from './SchedulePicker';

interface StepEditorProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  step: ScheduleStep;  // Required - edit mode only
  deviceId: string;
  scheduleId: string;
  deviceActions: Action[];
  deviceSchedules?: Schedule[];  // For nested schedule selection
  day?: DayOfWeek; // Optional: for weekly schedules, specifies the day the step belongs to
  scheduleType?: ScheduleType; // A choice board says "choice", not "step" (BA-124)
}

export const StepEditor: React.FC<StepEditorProps> = ({
  open,
  onClose,
  onSaved,
  step,
  deviceId,
  scheduleId,
  deviceActions,
  deviceSchedules = [],
  day,
  scheduleType,
}) => {
  const { t } = useTranslation();
  const isChoiceBoard = scheduleType === 'choice_board';
  const [actionPickerOpen, setActionPickerOpen] = useState(false);
  const [schedulePickerOpen, setSchedulePickerOpen] = useState(false);

  // Form state
  const [selectedAction, setSelectedAction] = useState<Action | null>(null);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);
  const [scheduledTime, setScheduledTime] = useState('');
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [enableTimer, setEnableTimer] = useState(false);
  const [timerDuration, setTimerDuration] = useState(60);
  const [timerWarningSeconds, setTimerWarningSeconds] = useState<number | undefined>(undefined);
  const [timerStyle, setTimerStyle] = useState<TimerStyle>('shrinking_disk');
  const [completionAction, setCompletionAction] = useState<CompletionAction>('tap');
  const [isSkippable, setIsSkippable] = useState(true);
  const [tokenValue, setTokenValue] = useState(0);
  const [helpPrompt, setHelpPrompt] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize form when dialog opens
  useEffect(() => {
    if (!open || !step) return;

    // Populate from existing step
    if (step.stepType === 'action' && step.actionId) {
      const action = deviceActions.find((a) => a.id === step.actionId);
      setSelectedAction(action || null);
      setSelectedSchedule(null);
    } else if (step.stepType === 'schedule' && step.scheduleId) {
      const nestedSchedule = deviceSchedules.find((s) => s.id === step.scheduleId);
      setSelectedSchedule(nestedSchedule || null);
      setSelectedAction(null);
    }

    setScheduledTime(step.scheduledTime || '');
    setTitle(step.title);
    setSubtitle(step.subtitle || '');
    setEnableTimer(!!step.timer);
    setTimerDuration(step.timer?.durationSeconds || 60);
    setTimerWarningSeconds(step.timer?.warningSeconds ?? undefined);
    setTimerStyle(step.timer?.style || 'shrinking_disk');
    setCompletionAction(step.completionAction);
    setIsSkippable(step.isSkippable);
    setTokenValue(step.tokenValue);
    setHelpPrompt(step.helpPrompt || '');
    setError(null);
  }, [step, open, deviceActions, deviceSchedules]);

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  const handleSelectAction = (action: Action) => {
    setSelectedAction(action);
    setActionPickerOpen(false);
  };

  const handleSelectSchedule = (schedule: Schedule) => {
    setSelectedSchedule(schedule);
    setSchedulePickerOpen(false);
  };

  const handleSave = async () => {
    if (!title.trim() || !step) return;

    setSaving(true);
    setError(null);

    try {
      const timerData = enableTimer
        ? {
            durationSeconds: timerDuration,
            warningSeconds: timerWarningSeconds,
            style: timerStyle,
          }
        : undefined;

      const stepData: StepUpdate = {
        title: title.trim(),
        subtitle: subtitle.trim() || undefined,
        stepType: step.stepType,
        actionId: step.stepType === 'action' ? selectedAction?.id : undefined,
        scheduleId: step.stepType === 'schedule' ? selectedSchedule?.id : undefined,
        scheduledTime: scheduledTime || undefined,
        timer: timerData,
        completionAction,
        isSkippable,
        tokenValue,
        helpPrompt: helpPrompt.trim() || undefined,
      };

      // Use day-specific endpoint for weekly schedules, regular endpoint otherwise
      if (day) {
        await scheduleService.updateDayStep(deviceId, scheduleId, day, step.id, stepData);
      } else {
        await scheduleService.updateStep(deviceId, scheduleId, step.id, stepData);
      }

      handleClose();
      onSaved();
    } catch (err: any) {
      console.error('Failed to update step:', err);
      if (err?.message?.includes('circular')) {
        setError(t('schedules.errors.circularReference'));
      } else {
        setError(t(isChoiceBoard ? 'schedules.choiceBoard.editDialog.failed' : 'schedules.errors.failedToUpdateStep'));
      }
    } finally {
      setSaving(false);
    }
  };

  const isValid = title.trim().length > 0;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t(isChoiceBoard ? 'schedules.choiceBoard.editDialog.title' : 'schedules.steps.editStep')}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Action/Schedule display based on step type */}
          {step?.stepType === 'action' ? (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('schedules.steps.linkedAction')}
              </Typography>

              {selectedAction ? (
                <Card
                  variant="outlined"
                  sx={{ mb: 2, cursor: 'pointer' }}
                  onClick={() => setActionPickerOpen(true)}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', py: 1.5 }}>
                    {selectedAction.thumbnailUrl || selectedAction.pictureUrl ? (
                      <Box
                        component="img"
                        src={selectedAction.thumbnailUrl || selectedAction.pictureUrl || ''}
                        alt={selectedAction.name}
                        sx={{
                          width: 48,
                          height: 48,
                          objectFit: 'cover',
                          borderRadius: 1,
                          mr: 2,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'grey.200',
                          borderRadius: 1,
                          mr: 2,
                        }}
                      >
                        <ImageIcon sx={{ color: 'grey.400' }} />
                      </Box>
                    )}
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1">{selectedAction.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('schedules.steps.clickToChange')}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ) : (
                <Card
                  variant="outlined"
                  sx={{
                    mb: 2,
                    border: '2px dashed',
                    borderColor: 'warning.main',
                    bgcolor: 'warning.50',
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography color="warning.dark">
                      {t('schedules.steps.actionNotFound')}
                    </Typography>
                    <Button
                      size="small"
                      sx={{ mt: 1 }}
                      onClick={() => setActionPickerOpen(true)}
                    >
                      {t('schedules.steps.selectNewAction')}
                    </Button>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('schedules.steps.linkedSchedule')}
              </Typography>

              {selectedSchedule ? (
                <Card
                  variant="outlined"
                  sx={{ mb: 2, cursor: 'pointer' }}
                  onClick={() => setSchedulePickerOpen(true)}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', py: 1.5 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.75rem',
                        mr: 2,
                      }}
                    >
                      {selectedSchedule.icon?.emoji || '📋'}
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1">{selectedSchedule.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('schedules.steps.clickToChange')}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ) : (
                <Card
                  variant="outlined"
                  sx={{
                    mb: 2,
                    border: '2px dashed',
                    borderColor: 'warning.main',
                    bgcolor: 'warning.50',
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography color="warning.dark">
                      {t('schedules.steps.scheduleNotFound')}
                    </Typography>
                    <Button
                      size="small"
                      sx={{ mt: 1 }}
                      onClick={() => setSchedulePickerOpen(true)}
                    >
                      {t('schedules.steps.selectNewSchedule')}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Scheduled time (for daily schedules) */}
              <TextField
                fullWidth
                type="time"
                label={t('schedules.steps.scheduledTime')}
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                sx={{ mb: 2 }}
                InputLabelProps={{ shrink: true }}
                helperText={t('schedules.steps.scheduledTimeHelp')}
                disabled={saving}
              />
            </>
          )}

          {/* Title */}
          <TextField
            fullWidth
            label={t(isChoiceBoard ? 'schedules.choiceBoard.editDialog.choiceTitle' : 'schedules.steps.stepTitle')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            sx={{ mb: 2 }}
            inputProps={{ maxLength: SCHEDULE_VALIDATION.STEP_TITLE_MAX_LENGTH }}
            disabled={saving}
          />

          {/* Subtitle */}
          <TextField
            fullWidth
            label={t('schedules.steps.stepSubtitle')}
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            sx={{ mb: 2 }}
            inputProps={{ maxLength: SCHEDULE_VALIDATION.STEP_SUBTITLE_MAX_LENGTH }}
            disabled={saving}
          />

          {/* Timer toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={enableTimer}
                onChange={(e) => setEnableTimer(e.target.checked)}
                disabled={saving}
              />
            }
            label={t('schedules.steps.timer')}
            sx={{ mb: 1 }}
          />

          {/* Timer configuration */}
          {enableTimer && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" gutterBottom>
                {t('schedules.steps.timerDuration')}: {timerDuration}s
              </Typography>
              <Slider
                value={timerDuration}
                onChange={(_, value) => setTimerDuration(value as number)}
                min={SCHEDULE_VALIDATION.TIMER_MIN_SECONDS}
                max={600}
                step={5}
                marks={[
                  { value: 30, label: '30s' },
                  { value: 60, label: '1m' },
                  { value: 120, label: '2m' },
                  { value: 300, label: '5m' },
                  { value: 600, label: '10m' },
                ]}
                disabled={saving}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                type="number"
                label={t('schedules.steps.timerWarning')}
                value={timerWarningSeconds || ''}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  setTimerWarningSeconds(val > 0 ? val : undefined);
                }}
                helperText={t('schedules.steps.timerWarningHelp')}
                inputProps={{ min: 5, max: timerDuration - 5 }}
                disabled={saving}
                sx={{ mb: 2 }}
              />

              <FormControl fullWidth>
                <InputLabel>{t('schedules.steps.timerStyle')}</InputLabel>
                <Select
                  value={timerStyle}
                  onChange={(e) => setTimerStyle(e.target.value as TimerStyle)}
                  label={t('schedules.steps.timerStyle')}
                  disabled={saving}
                >
                  <MenuItem value="shrinking_disk">{t('schedules.timerStyles.shrinking_disk')}</MenuItem>
                  <MenuItem value="shrinking_bar">{t('schedules.timerStyles.shrinking_bar')}</MenuItem>
                  <MenuItem value="countdown">{t('schedules.timerStyles.countdown')}</MenuItem>
                  <MenuItem value="hourglass">{t('schedules.timerStyles.hourglass')}</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Completion action */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('schedules.steps.completionAction')}</InputLabel>
            <Select
              value={completionAction}
              onChange={(e) => setCompletionAction(e.target.value as CompletionAction)}
              label={t('schedules.steps.completionAction')}
              disabled={saving}
            >
              <MenuItem value="tap">{t('schedules.completionActions.tap')}</MenuItem>
              <MenuItem value="swipe">{t('schedules.completionActions.swipe')}</MenuItem>
              <MenuItem value="hold">{t('schedules.completionActions.hold')}</MenuItem>
              <MenuItem value="auto">{t('schedules.completionActions.auto')}</MenuItem>
              <MenuItem value="parent_only">{t('schedules.completionActions.parent_only')}</MenuItem>
            </Select>
          </FormControl>

          {/* Skippable toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={isSkippable}
                onChange={(e) => setIsSkippable(e.target.checked)}
                disabled={saving}
              />
            }
            label={t('schedules.steps.skippable')}
            sx={{ mb: 2 }}
          />

          {/* Token value */}
          <TextField
            fullWidth
            type="number"
            label={t('schedules.steps.tokenValue')}
            value={tokenValue}
            onChange={(e) =>
              setTokenValue(Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))
            }
            inputProps={{
              min: SCHEDULE_VALIDATION.TOKEN_MIN_VALUE,
              max: SCHEDULE_VALIDATION.TOKEN_MAX_VALUE,
            }}
            disabled={saving}
            sx={{ mb: 2 }}
          />

          {/* Help prompt */}
          <TextField
            fullWidth
            label={t('schedules.steps.helpPrompt')}
            value={helpPrompt}
            onChange={(e) => setHelpPrompt(e.target.value)}
            multiline
            rows={2}
            helperText={t('schedules.steps.helpPromptHelp')}
            disabled={saving}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!isValid || saving}
            startIcon={saving && <CircularProgress size={16} />}
          >
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action picker dialog */}
      <ActionPicker
        open={actionPickerOpen}
        onClose={() => setActionPickerOpen(false)}
        onSelect={handleSelectAction}
        actions={deviceActions}
      />

      {/* Schedule picker dialog */}
      <SchedulePicker
        open={schedulePickerOpen}
        onClose={() => setSchedulePickerOpen(false)}
        onSelect={handleSelectSchedule}
        schedules={deviceSchedules}
        currentScheduleId={scheduleId}
      />
    </>
  );
};
